import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { apiGet, apiPost, getToken, setToken } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import type { Me, Role } from '@/types/api';

interface AuthState {
  me: Me | null;
  status: 'idle' | 'loading' | 'authed' | 'unauthed';
  loginPin: (phone10: string, pin: string) => Promise<void>;
  /** OTP login = sendOtp → verifyOtp. Returned token completes auth.
   *  Onboarding is NOT supported here — the panel is for existing
   *  tenants only. New users sign up via the mobile app. */
  sendOtp: (phone10: string) => Promise<{ devOtp?: string }>;
  verifyOtp: (phone10: string, otp: string) => Promise<void>;
  logout: () => Promise<void>;
  /** True if `me` has any of the supplied roles. */
  hasRole: (...roles: Role[]) => boolean;
  refreshMe: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('idle');

  const refreshMe = useCallback(async () => {
    setStatus('loading');
    try {
      const data = await apiGet<Me>(endpoints.me);
      setMe(data);
      setStatus('authed');
    } catch {
      setMe(null);
      setStatus('unauthed');
      // Token likely invalid — wipe so we don't keep retrying.
      setToken(null);
    }
  }, []);

  // Boot — if we have a token, try to resolve the user. Otherwise stay
  // unauthed and let the route guard send to /login.
  useEffect(() => {
    if (getToken()) {
      void refreshMe();
    } else {
      setStatus('unauthed');
    }
  }, [refreshMe]);

  const loginPin: AuthState['loginPin'] = useCallback(async (phone10, pin) => {
    const res = await apiPost<{ token: string; user: Me }>(endpoints.loginPin, {
      phone: phone10,
      pin,
    });
    setToken(res.token);
    setMe(res.user);
    setStatus('authed');
  }, []);

  const sendOtp: AuthState['sendOtp'] = useCallback(async (phone10) => {
    const res = await apiPost<{ otp?: string; dev_mode?: boolean } | null>(
      endpoints.sendOtp,
      { phone: phone10 },
    );
    return { devOtp: (res?.otp as string | undefined) ?? undefined };
  }, []);

  const verifyOtp: AuthState['verifyOtp'] = useCallback(async (phone10, otp) => {
    // The backend returns { token, user, is_new } for existing users, OR
    // { phone, is_new: true } when the phone hasn't onboarded yet.
    const data = await apiPost<{ token?: string; user?: Me; is_new?: boolean }>(
      endpoints.verifyOtp,
      { phone: phone10, otp },
    );
    if (data.is_new || !data.token) {
      throw new Error(
        'This phone has not finished onboarding. Open the New Vishwas Electronics mobile app to complete setup.',
      );
    }
    setToken(data.token);
    if (data.user) setMe(data.user);
    setStatus('authed');
    if (!data.user) await refreshMe();
  }, [refreshMe]);

  const logout: AuthState['logout'] = useCallback(async () => {
    try {
      await apiPost(endpoints.logout);
    } catch {
      // Ignore — we wipe locally regardless so the user always escapes.
    }
    setToken(null);
    setMe(null);
    setStatus('unauthed');
  }, []);

  const hasRole = useCallback(
    (...roles: Role[]) => {
      if (!me) return false;
      return roles.some((r) => me.roles.includes(r));
    },
    [me],
  );

  const value = useMemo<AuthState>(
    () => ({ me, status, loginPin, sendOtp, verifyOtp, logout, hasRole, refreshMe }),
    [me, status, loginPin, sendOtp, verifyOtp, logout, hasRole, refreshMe],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
